import React, { useState, useEffect, useRef, Suspense } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { Canvas } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import SplashCursor from "../src/SplashCursor";

// --- ASSET IMPORTS ---
import profilePhoto from "./profile_pic_anant1.jpg";
import resume from "./assets/Anant_resume.pdf";

// --- DATA CONFIGURATION (EDIT YOUR DETAILS HERE) ---
const PORTFOLIO_DATA = {
  basicInfo: {
    name: "Anant Mulay",
    profilePhoto: profilePhoto,
    titles: [
      "Full Stack Developer",
      2000,
      "AI & ML Enthusiast",
      2000,
      "Android Developer",
      2000,
      "React Specialist",
      2000,
    ],
    bio: "I build intelligent, scalable, and user-centric web and mobile applications by blending full-stack expertise with a passion for Artificial Intelligence.",
  },
  contact: {
    email: "apmmulay@gmail.com",
    phone: "+91 9503010083",
    location: "Kolhapur, Maharashtra, India",
    socials: {
      linkedin: "https://www.linkedin.com/in/anant-mulay-bb6623202/",
      github: "https://github.com/AnantM20",
    },
    resumeUrl: resume,
  },
  about: {
    detailedBio:
      "As a developer with two years of hands-on experience, I've honed my skills across the entire software development lifecycle. My journey began with a curiosity for building things, which evolved into a career focused on creating high-quality, impactful software. I am adept at translating complex requirements into functional, elegant solutions and thrive in collaborative environments where I can both contribute and learn.",
    missionStatement:
      "To leverage cutting-edge technologies and best practices to build robust, scalable, and intelligent applications that solve real-world problems and deliver exceptional user experiences.",
  },
  skills: {
    technical: [
      { name: "React.js / Redux", level: 90 },
      { name: "Node.js / Express.js", level: 85 },
      { name: ".NET Core / ASP.NET", level: 80 },
      { name: "Python (AI/ML)", level: 85 },
      { name: "Java / Kotlin (Android)", level: 80 },
      { name: "SQL & NoSQL", level: 85 },
      { name: "Cloud (Azure, AWS)", level: 75 },
    ],
    soft: [
      "Effective Communication",
      "Problem-Solving",
      "Team Collaboration",
      "Agile Methodologies",
      "Adaptability",
    ],
    tools: [
      "Git & GitHub",
      "Docker",
      "Android Studio",
      "VS Code",
      "Jira",
      "Figma",
    ],
  },
  projects: [
    {
      title: "BPCL Financial Systems UI",
      description:
        "Led the frontend architecture and development for a critical financial dashboard, focusing on performance, modularity, and real-time data visualization using React and Redux.",
      techStack: ["React", "Redux", ".NET Core API", "CSS-in-JS"],
      imageUrl: "https://via.placeholder.com/1280x720.png?text=BPCL+Project",
      githubLink: "https://github.com/AnantM20",
    },
    {
      title: "Quick Heal AI Data Extractor",
      description:
        "Developed and trained a deep learning model for Optical Character Recognition (OCR) and PII data extraction, achieving over 95% accuracy on test datasets.",
      techStack: ["Python", "TensorFlow", "Scikit-learn", "MySQL"],
      imageUrl: "https://via.placeholder.com/1280x720.png?text=AI+Project",
      githubLink: "https://github.com/AnantM20",
    },
  ],
  experience: [
    {
      company: "The DataTech Lab",
      role: "Full Stack Developer",
      duration: "Nov 2023 - Present",
      responsibilities: [
        "Architect and develop full-stack features for web applications using the MERN stack and .NET.",
        "Integrate AI/ML models to enhance application functionality, including NLP and data analysis.",
        "Lead code reviews and mentor junior developers, promoting best practices in software development.",
      ],
    },
    {
      company: "Tron Softech",
      role: "Software Developer Intern (Android Developer)",
      duration: "May 2023 - Aug 2023",
      responsibilities: [
        "Contributed to the development of native Android applications using Java and Kotlin.",
        "Assisted in building responsive UI components and implementing new features for Android apps.",
        "Participated in the full agile development lifecycle, from sprint planning to deployment.",
        "Identified and resolved bugs, contributing to the overall stability and performance of the applications.",
      ],
    },
  ],
  education: {
    degree: "Bachelor of Technology in Computer Engineering",
    institution: "Shivaji University",
    duration: "2020 - 2024",
    highlights:
      "Graduated with a CGPA of 8.9/10, focusing on data structures, algorithms, and AI.",
  },
  certifications: [
    { name: "Certified CyberOps Associate", issuer: "Cisco" },
    { name: "Microsoft Azure Fundamentals", issuer: "Microsoft" },
  ],
  testimonials: [
    {
      feedback:
        "Anant is a highly skilled and dedicated developer. His ability to tackle complex problems and deliver high-quality code was a huge asset to our team.",
      name: "Tushar Rajput (Sr. Software Engineer)",
      role: "Project Guide, The DataTech Lab",
    },
  ],
};

// --- THEME & STYLES CONFIG ---
const theme = {
  colors: {
    background: "#0a192f",
    text: "#8892b0",
    textHighlight: "#ccd6f6",
    primary: "#64ffda",
    card: "rgba(30, 41, 59, 0.75)",
    cardBorder: "#475569",
    cardHighlight: "#1f4068",
  },
};

// --- FRAMER MOTION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// --- HELPER HOOK FOR HOVER STATE ---
const useHover = () => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };
  return [isHovered, hoverProps];
};

// --- MAIN PORTFOLIO COMPONENT ---
const Portfolio = () => {
  // Inject Global Styles

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
            body { background-color: ${theme.colors.background}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
            ::-webkit-scrollbar { width: 8px; }
            ::-webkit-scrollbar-track { background: ${theme.colors.background}; }
            ::-webkit-scrollbar-thumb { background: ${theme.colors.cardHighlight}; border-radius: 4px; }
            ::-webkit-scrollbar-thumb:hover { background: ${theme.colors.cardBorder}; }
        `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // --- SUB-COMPONENT DEFINITIONS ---

  const AnimatedSection = ({ children, id }) => {
    const controls = useAnimation();
    const ref = useRef(null);
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) controls.start("visible");
        },
        { threshold: 0.1 }
      );
      const currentRef = ref.current;
      if (currentRef) observer.observe(currentRef);
      return () => {
        if (currentRef) observer.unobserve(currentRef);
      };
    }, [controls]);
    return (
      <motion.section
        id={id}
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        style={{ padding: "6rem 0" }}
      >
        <motion.h2
          variants={itemVariants}
          style={{
            fontSize: "clamp(26px, 5vw, 32px)",
            color: theme.colors.textHighlight,
            marginBottom: "3rem",
            position: "relative",
            paddingLeft: "1.5rem",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              height: "50%",
              width: "4px",
              backgroundColor: theme.colors.primary,
              borderRadius: "2px",
            }}
          ></span>
          {id.charAt(0).toUpperCase() + id.slice(1)}
        </motion.h2>
        {children}
      </motion.section>
    );
  };

  const NavLink = ({ link, index }) => {
    const [isHovered, hoverProps] = useHover();
    return (
      <motion.li
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 + index * 0.1 }}
      >
        <a
          href={`#${link}`}
          {...hoverProps}
          style={{
            color: isHovered
              ? theme.colors.primary
              : theme.colors.textHighlight,
            textDecoration: "none",
            transition: "color 0.3s",
            textTransform: "capitalize",
          }}
        >
          {link}
        </a>
      </motion.li>
    );
  };

  const Header = () => {
    const navLinks = ["about", "experience", "projects", "contact"];
    return (
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 100,
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(10, 25, 47, 0.85)",
          boxSizing: "border-box",
        }}
      >
        <a
          href="#hero"
          style={{
            color: theme.colors.primary,
            fontFamily: "monospace",
            fontSize: "1.5rem",
            fontWeight: "bold",
            textDecoration: "none",
          }}
        >
          AM
        </a>
        <nav>
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: "2rem",
              margin: 0,
              padding: 0,
            }}
          >
            {navLinks.map((link, i) => (
              <NavLink key={link} link={link} index={i} />
            ))}
          </ul>
        </nav>
      </motion.header>
    );
  };

  const Hero = ({ data, resumeUrl }) => (
    <section
      id="hero"
      style={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          alignItems: "center",
          gap: "3rem",
        }}
      >
        <div>
          <motion.p
            variants={itemVariants}
            style={{
              color: theme.colors.primary,
              fontFamily: "monospace",
              marginBottom: "1rem",
            }}
          >
            Hi, my name is
          </motion.p>
          <motion.h1
            variants={itemVariants}
            style={{
              fontSize: "clamp(40px, 8vw, 70px)",
              fontWeight: "bold",
              color: theme.colors.textHighlight,
              margin: 0,
            }}
          >
            {data.name}
          </motion.h1>
          <motion.div
            variants={itemVariants}
            style={{
              fontSize: "clamp(20px, 5vw, 40px)",
              fontWeight: "bold",
              color: theme.colors.text,
              marginTop: "0.5rem",
            }}
          >
            <TypeAnimation
              sequence={data.titles}
              wrapper="span"
              speed={50}
              repeat={Infinity}
            />
          </motion.div>
          <motion.p
            variants={itemVariants}
            style={{ maxWidth: "540px", marginTop: "1.5rem" }}
          >
            {data.bio}
          </motion.p>
          <motion.a
            variants={itemVariants}
            href={resumeUrl}
            download
            style={{
              display: "inline-block",
              color: theme.colors.primary,
              border: `1px solid ${theme.colors.primary}`,
              borderRadius: "0.375rem",
              padding: "0.75rem 1.5rem",
              fontFamily: "monospace",
              marginTop: "2rem",
              textDecoration: "none",
              transition: "background-color 0.3s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(100, 255, 218, 0.1)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            Download CV
          </motion.a>
        </div>
        <motion.div
          variants={itemVariants}
          style={{
            position: "relative",
            width: "300px",
            height: "300px",
            justifySelf: "center",
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: `2px solid ${theme.colors.primary}`,
              boxShadow: `0 0 20px ${theme.colors.primary}, inset 0 0 20px ${theme.colors.primary}`,
            }}
          />
          <img
            src={data.profilePhoto}
            alt="Anant Mulay"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
              border: `4px solid ${theme.colors.background}`,
            }}
          />
        </motion.div>
      </motion.div>
    </section>
  );

  const SocialLinks = ({ socials, email }) => {
    const [isEmailHovered, emailHoverProps] = useHover();
    const SocialIcon = ({ href, children }) => {
      const [isHovered, hoverProps] = useHover();
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...hoverProps}
          style={{
            color: isHovered ? theme.colors.primary : theme.colors.text,
            transform: isHovered ? "translateY(-3px)" : "translateY(0)",
            transition: "color 0.3s, transform 0.3s",
          }}
        >
          {children}
        </a>
      );
    };

    return (
      <>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            position: "fixed",
            bottom: 0,
            left: "3rem",
            zIndex: 10,
            alignItems: "center",
          }}
        >
          <SocialIcon href={socials.github}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ width: "1.5rem", height: "1.5rem" }}
            >
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
            </svg>
          </SocialIcon>
          <SocialIcon href={socials.linkedin}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ width: "1.5rem", height: "1.5rem" }}
            >
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
              <rect x="2" y="9" width="4" height="12"></rect>
              <circle cx="4" cy="4" r="2"></circle>
            </svg>
          </SocialIcon>
          <div
            style={{
              height: "6rem",
              width: "1px",
              backgroundColor: theme.colors.text,
            }}
          ></div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            position: "fixed",
            bottom: 0,
            right: "3rem",
            zIndex: 10,
            alignItems: "center",
          }}
        >
          <a
            href={`mailto:${email}`}
            {...emailHoverProps}
            style={{
              fontSize: "0.875rem",
              letterSpacing: "0.1em",
              writingMode: "vertical-rl",
              transition: "color 0.3s, transform 0.3s",
              textDecoration: "none",
              color: isEmailHovered ? theme.colors.primary : theme.colors.text,
              transform: isEmailHovered ? "translateY(-5px)" : "translateY(0)",
            }}
          >
            {email}
          </a>
          <div
            style={{
              height: "6rem",
              width: "1px",
              backgroundColor: theme.colors.text,
            }}
          ></div>
        </motion.div>
      </>
    );
  };

  const About = ({ data }) => (
    <AnimatedSection id="about">
      <motion.div
        variants={itemVariants}
        style={{ ...styles.card, padding: "2rem" }}
      >
        <p style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>
          {data.detailedBio}
        </p>
        <p style={{ fontStyle: "italic" }}>
          <strong>Mission:</strong> {data.missionStatement}
        </p>
      </motion.div>
    </AnimatedSection>
  );

  const Skills = ({ data }) => (
    <AnimatedSection id="skills">
      <motion.div variants={itemVariants}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "3rem",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "1.5rem",
                color: theme.colors.textHighlight,
                marginBottom: "1.5rem",
              }}
            >
              Technical Skills
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
              }}
            >
              {data.technical.map((skill) => (
                <div key={skill.name}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>{skill.name}</span>
                    <span
                      style={{
                        color: theme.colors.primary,
                        fontFamily: "monospace",
                      }}
                    >
                      {skill.level}%
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: theme.colors.cardHighlight,
                      borderRadius: "9999px",
                      height: "8px",
                    }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true, amount: 0.8 }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      style={{
                        backgroundColor: theme.colors.primary,
                        height: "100%",
                        borderRadius: "9999px",
                      }}
                    ></motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "3rem" }}
          >
            <div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  color: theme.colors.textHighlight,
                  marginBottom: "1.5rem",
                }}
              >
                Soft Skills
              </h3>
              <ul
                style={{
                  listStyleType: "none",
                  padding: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                {data.soft.map((s) => (
                  <li
                    key={s}
                    style={{
                      backgroundColor: theme.colors.card,
                      border: `1px solid ${theme.colors.cardBorder}`,
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                    }}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  color: theme.colors.textHighlight,
                  marginBottom: "1.5rem",
                }}
              >
                Tools I Use
              </h3>
              <ul
                style={{
                  listStyleType: "none",
                  padding: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                {data.tools.map((t) => (
                  <li
                    key={t}
                    style={{
                      backgroundColor: theme.colors.card,
                      border: `1px solid ${theme.colors.cardBorder}`,
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                    }}
                  >
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatedSection>
  );

  const Experience = ({ data }) => {
    const ExperienceCard = ({ job }) => {
      const [isHovered, hoverProps] = useHover();
      return (
        <motion.div
          variants={itemVariants}
          {...hoverProps}
          style={{
            padding: "1.5rem",
            borderRadius: "8px",
            ...styles.card,
            borderLeft: `4px solid ${
              isHovered ? theme.colors.primary : theme.colors.cardBorder
            }`,
            transition: "border-left 0.3s, transform 0.3s",
            transform: isHovered ? "scale(1.02)" : "scale(1)",
          }}
        >
          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              color: theme.colors.textHighlight,
            }}
          >
            {job.role} @{" "}
            <span style={{ color: theme.colors.primary }}>{job.company}</span>
          </h3>
          <p
            style={{
              fontFamily: "monospace",
              fontSize: "0.875rem",
              marginBottom: "1rem",
            }}
          >
            {job.duration}
          </p>
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            {job.responsibilities.map((r, i) => (
              <li
                key={i}
                style={{ position: "relative", paddingLeft: "1.5rem" }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    color: theme.colors.primary,
                  }}
                >
                  ▹
                </span>
                {r}
              </li>
            ))}
          </ul>
        </motion.div>
      );
    };
    return (
      <AnimatedSection id="experience">
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          {data.map((job, index) => (
            <ExperienceCard key={index} job={job} />
          ))}
        </div>
      </AnimatedSection>
    );
  };

  const Projects = ({ data }) => {
    const ProjectCard = ({ project }) => {
      const [isHovered, hoverProps] = useHover();
      return (
        <motion.div
          variants={itemVariants}
          {...hoverProps}
          style={{
            ...styles.card,
            borderRadius: "8px",
            overflow: "hidden",
            transition: "transform 0.3s, box-shadow 0.3s",
            transform: isHovered ? "translateY(-10px)" : "translateY(0)",
            boxShadow: isHovered
              ? `0 20px 25px -5px ${theme.colors.background}`
              : "none",
          }}
        >
          <img
            src={project.imageUrl}
            alt={project.title}
            style={{
              width: "100%",
              display: "block",
              borderBottom: `1px solid ${theme.colors.cardBorder}`,
            }}
          />
          <div style={{ padding: "1.5rem" }}>
            <h3
              style={{
                color: theme.colors.primary,
                fontSize: "1.25rem",
                marginBottom: "1rem",
              }}
            >
              {project.title}
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>{project.description}</p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
                marginBottom: "1.5rem",
                fontFamily: "monospace",
              }}
            >
              {project.techStack.map((tech) => (
                <span
                  key={tech}
                  style={{
                    backgroundColor: theme.colors.cardHighlight,
                    color: theme.colors.primary,
                    padding: "0.25rem 0.75rem",
                    borderRadius: "9999px",
                    fontSize: "0.75rem",
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
            <a
              href={project.githubLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.colors.textHighlight,
                textDecoration: "none",
                fontWeight: "bold",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.color = theme.colors.primary)
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.color = theme.colors.textHighlight)
              }
            >
              View Code &rarr;
            </a>
          </div>
        </motion.div>
      );
    };
    return (
      <AnimatedSection id="projects">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "2rem",
          }}
        >
          {data.map((project, i) => (
            <ProjectCard key={i} project={project} />
          ))}
        </div>
      </AnimatedSection>
    );
  };

  const EducationAndCerts = ({ education, certifications }) => (
    <AnimatedSection id="education">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "3rem",
        }}
      >
        <motion.div variants={itemVariants}>
          <h3
            style={{
              fontSize: "1.5rem",
              color: theme.colors.textHighlight,
              marginBottom: "1.5rem",
            }}
          >
            Education
          </h3>
          <div style={styles.card}>
            <div style={{ padding: "1.5rem" }}>
              <h4
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: theme.colors.textHighlight,
                  margin: 0,
                }}
              >
                {education.degree}
              </h4>
              <p style={{ color: theme.colors.primary, margin: "0.5rem 0" }}>
                {education.institution} | {education.duration}
              </p>
              <p style={{ margin: "0.5rem 0 0 0" }}>{education.highlights}</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={itemVariants}>
          <h3
            style={{
              fontSize: "1.5rem",
              color: theme.colors.textHighlight,
              marginBottom: "1.5rem",
            }}
          >
            Certifications
          </h3>
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            {certifications.map((c) => (
              <li key={c.name} style={{ ...styles.card, padding: "1rem" }}>
                <strong>{c.name}</strong> - {c.issuer}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </AnimatedSection>
  );

  const Testimonials = ({ data }) => (
    <AnimatedSection id="testimonials">
      {data.map((t, i) => (
        <motion.div
          key={i}
          variants={itemVariants}
          style={{ ...styles.card, padding: "2rem", textAlign: "center" }}
        >
          <p style={{ fontSize: "1.25rem", fontStyle: "italic" }}>
            "{t.feedback}"
          </p>
          <p
            style={{
              marginTop: "1rem",
              fontWeight: "bold",
              color: theme.colors.textHighlight,
            }}
          >
            {t.name}
          </p>
          <p style={{ fontSize: "0.875rem" }}>{t.role}</p>
        </motion.div>
      ))}
    </AnimatedSection>
  );

  const Contact = () => {
    const [formData, setFormData] = useState({
      name: "",
      email: "",
      message: "",
    });
    const handleFormChange = (e) =>
      setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleFormSubmit = (e) => {
      e.preventDefault();
      // In a real app, you would handle form submission here (e.g., send to an API)
      alert("Thank you for your message!");
      setFormData({ name: "", email: "", message: "" });
    };

    const formInputStyle = {
      width: "100%",
      backgroundColor: theme.colors.cardHighlight,
      border: `1px solid ${theme.colors.cardBorder}`,
      color: theme.colors.textHighlight,
      padding: "1rem",
      borderRadius: "8px",
      boxSizing: "border-box",
    };

    return (
      <AnimatedSection id="contact">
        <motion.div
          variants={itemVariants}
          style={{ maxWidth: "40rem", margin: "0 auto", textAlign: "center" }}
        >
          <p style={{ marginBottom: "2rem" }}>
            I'm currently open to new opportunities. Whether you have a question
            or just want to say hi, my inbox is always open. I'll get back to
            you as soon as I can!
          </p>
          <form
            onSubmit={handleFormSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              placeholder="Your Name"
              required
              style={formInputStyle}
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              placeholder="Your Email"
              required
              style={formInputStyle}
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              placeholder="Your Message"
              required
              style={{
                ...formInputStyle,
                minHeight: "150px",
                resize: "vertical",
              }}
            ></textarea>
            <button
              type="submit"
              style={{
                color: theme.colors.primary,
                border: `1px solid ${theme.colors.primary}`,
                borderRadius: "8px",
                padding: "1rem 1.5rem",
                fontFamily: "monospace",
                fontSize: "1rem",
                cursor: "pointer",
                backgroundColor: "transparent",
                transition: "background-color 0.3s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(100, 255, 218, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              Send Message
            </button>
          </form>
        </motion.div>
      </AnimatedSection>
    );
  };

  const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    // A minimal chatbot UI for brevity. The full logic can be expanded here.
    return (
      <div
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          zIndex: 1000,
        }}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              style={{
                position: "absolute",
                bottom: "70px",
                right: 0,
                width: "300px",
                height: "400px",
                backgroundColor: theme.colors.background,
                border: `1px solid ${theme.colors.primary}`,
                borderRadius: "15px",
                display: "flex",
                flexDirection: "column",
                zIndex: 999,
                boxShadow: "0 10px 30px -15px rgba(0,0,0,0.7)",
              }}
            >
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: theme.colors.cardHighlight,
                  borderTopLeftRadius: "15px",
                  borderTopRightRadius: "15px",
                }}
              >
                <h3 style={{ color: theme.colors.textHighlight, margin: 0 }}>
                  Chatbot
                </h3>
              </div>
              <div
                style={{ flex: 1, padding: "1rem", color: theme.colors.text }}
              >
                Hello! How can I help?
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <motion.button
          whileHover={{ scale: 1.1 }}
          onClick={() => setIsOpen(!isOpen)}
          style={{
            backgroundColor: theme.colors.primary,
            color: theme.colors.background,
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            border: "none",
            boxShadow: "0 4px 15px rgba(100, 255, 218, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "1.5rem",
          }}
        >
          {isOpen ? "✕" : "💬"}
        </motion.button>
      </div>
    );
  };

  const ScrollToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisibility = () =>
      window.pageYOffset > 300 ? setIsVisible(true) : setIsVisible(false);
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    useEffect(() => {
      window.addEventListener("scroll", toggleVisibility);
      return () => window.removeEventListener("scroll", toggleVisibility);
    }, []);

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.button
            onClick={scrollToTop}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            style={{
              position: "fixed",
              bottom: "2rem",
              left: "2rem",
              backgroundColor: theme.colors.primary,
              color: theme.colors.background,
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 15px rgba(100, 255, 218, 0.4)",
              fontSize: "1.5rem",
            }}
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>
    );
  };

  // --- MAIN RENDER ---
  return (
    <div style={{ color: theme.colors.text }}>
      <SplashCursor />

      <Canvas
        camera={{ position: [0, 0, 5] }}
        style={{ position: "fixed", top: 0, left: 0, zIndex: -1 }}
      >
        <Suspense fallback={null}>
          <Stars
            radius={100}
            depth={50}
            count={5000}
            factor={4}
            saturation={0}
            fade
          />
        </Suspense>
      </Canvas>

      <Header />
      <SocialLinks
        socials={PORTFOLIO_DATA.contact.socials}
        email={PORTFOLIO_DATA.contact.email}
      />

      <main
        style={{ margin: "0 auto", maxWidth: "1000px", padding: "0 1.5rem" }}
      >
        <Hero
          data={PORTFOLIO_DATA.basicInfo}
          resumeUrl={PORTFOLIO_DATA.contact.resumeUrl}
        />
        <About data={PORTFOLIO_DATA.about} />
        <Experience data={PORTFOLIO_DATA.experience} />
        <Projects data={PORTFOLIO_DATA.projects} />
        <Skills data={PORTFOLIO_DATA.skills} />
        <EducationAndCerts
          education={PORTFOLIO_DATA.education}
          certifications={PORTFOLIO_DATA.certifications}
        />
        <Testimonials data={PORTFOLIO_DATA.testimonials} />
        <Contact />
      </main>

      <Chatbot />
      <ScrollToTopButton />

      <footer
        style={{
          textAlign: "center",
          padding: "2rem",
          fontFamily: "monospace",
          fontSize: "0.8rem",
        }}
      >
        <div>Designed & Built by Anant Mulay</div>
      </footer>
    </div>
  );
};

// Dummy styles object for any shared inline styles if needed
const styles = {
  card: {
    backgroundColor: theme.colors.card,
    border: `1px solid ${theme.colors.cardBorder}`,
    backdropFilter: "blur(10px)",
  },
};

export default Portfolio;
